import { CustomerData } from "../../interfaces/CustomerData/CustomerData";
import ProductProperties from "../../interfaces/ProductProperties/ProductProperties";
import SessionStorageProviderProps, { SessionStorageItems } from "./SessionStorageProvider.props";

interface DirectPurchaseData {
    productProperties: ProductProperties;
    campaignId?: string;
    timestamp?: number;
}

export default class SessionStorageProvider {
    private props: SessionStorageProviderProps;
    private warenkorbData: Array<ProductProperties>;
    private warenkorbDataCampaign: Array<ProductProperties>;
    private customerData: CustomerData;
    private campaign: string;

    constructor(props: SessionStorageProviderProps) {
        this.props = props;
        this.warenkorbData = [];
        this.warenkorbDataCampaign = [];
        this.customerData = {};
        this.campaign = "";
        this.updateSessionStorageProvider();
    }
    
    updateSessionStorageProvider() {
        const warenkorbData = sessionStorage.getItem(SessionStorageItems.CART) || "[]";
        // TODO: Base64 Decoding
        // if(environment.isProductionBuild()) {
        //     const buff = Buffer.from(lsData, 'base64');
        //     lsData = buff.toString('utf8');
        // }
        this.warenkorbData = JSON.parse(warenkorbData) as ProductProperties[];
        
        const customerData = sessionStorage.getItem(SessionStorageItems.CUSTOMER) || '{}';
        this.customerData = JSON.parse(customerData) as CustomerData;
    }
    
    private writeToSessionStorage(item: SessionStorageItems, data: unknown) {
        // Automatically stringify non-string data. If data is already a string, store as-is.
        const dataString = typeof data === 'string' ? data : JSON.stringify(data);
        // TODO: Base64 Encoding
        // if(environment.isProductionBuild()) {
        //     const data = productInformationString;
        //     const buff = Buffer.from(data, 'base64');
        //     sessionStorage.setItem("warenkorb", buff as unknown as string);
        // } else {
        sessionStorage.setItem(item, dataString);
        // }
    }

    getProductInformationFromSessionStorage(magicNumber?: string): { productProperties?: ProductProperties, warenkorbData: ProductProperties[] } | undefined {
        return {
            productProperties: magicNumber ? this.warenkorbData.find((productProperties) => productProperties.magicNumber === magicNumber) : undefined,
            warenkorbData: this.warenkorbData
        }
    }

    setProductInformationInSessionStorage(productProperties: ProductProperties[]) {
        this.writeToSessionStorage(SessionStorageItems.CART, productProperties);
        this.updateSessionStorageProvider();
    }

    
    getCustomerDataFromSessionStorage(): CustomerData {
        return this.customerData;
    }

    setCustomerDataInSessionStorage(customerData: CustomerData) {
        this.writeToSessionStorage(SessionStorageItems.CUSTOMER, customerData);
        this.updateSessionStorageProvider();
    }

    getCampaignFromSessionStorage(): string | null {
        return sessionStorage.getItem(SessionStorageItems.CAMPAIGN);
    }

    setCampaignInSessionStorage(campaign: string) {
        // campaign is a string, writeToSessionStorage will store it without JSON.stringify
        this.writeToSessionStorage(SessionStorageItems.CAMPAIGN, campaign);
        this.updateSessionStorageProvider();
    }
    
    updateSessionStorageProviderCampaign(campaignId: string, pageType: string) {
        const warenkorbData = sessionStorage.getItem(`${pageType}_${campaignId}_${SessionStorageItems.CART}`) || "[]";
        // TODO: Base64 Decoding
        // if(environment.isProductionBuild()) {
        //     const buff = Buffer.from(lsData, 'base64');
        //     lsData = buff.toString('utf8');
        // }
        this.warenkorbDataCampaign = JSON.parse(warenkorbData) as ProductProperties[];
        
        const customerData = sessionStorage.getItem(SessionStorageItems.CUSTOMER) || '{}';
        this.customerData = JSON.parse(customerData) as CustomerData;
    }
    
    private writeToSessionStorageCampaign(item: string, data: unknown) {
        const dataString = JSON.stringify(data);
        sessionStorage.setItem(item, dataString);
    }

    getProductInformationFromSessionStorageCampaign(magicNumber?: string): { productProperties?: ProductProperties, warenkorbData: ProductProperties[] } | undefined {
        // console.log('warenkorbCampaign: ', this.warenkorbDataCampaign)
        return {
            productProperties: magicNumber ? this.warenkorbDataCampaign.find((productProperties) => productProperties.magicNumber === magicNumber) : undefined,
            warenkorbData: this.warenkorbDataCampaign
        }
    }

    setProductInformationInSessionStorageForCampaign(productProperties: ProductProperties[], campaignId: string, pageType: string) {
        this.writeToSessionStorageCampaign(`${pageType}_${campaignId}_${SessionStorageItems.CART}`, productProperties);
        this.updateSessionStorageProviderCampaign(campaignId, pageType);
    }

    setCounterForSteps(campaignId: string, pageType: string, step: string) {
        this.writeToSessionStorageCampaign(`${pageType}_${campaignId}_${step}`, step);
    }

    getCounterForSteps(campaignId: string, pageType: string, step: string) {
        return sessionStorage.getItem(`${pageType}_${campaignId}_${step}`);
    }

    // Direct Purchase functionality
    setDirectPurchaseInSessionStorage(productProperties: ProductProperties, campaignId?: string): void {
        const directPurchaseData: DirectPurchaseData = {
            productProperties,
            campaignId,
            timestamp: Date.now()
        };
        this.writeToSessionStorage(SessionStorageItems.DIRECT_PURCHASE, directPurchaseData);
    }

    getDirectPurchaseFromSessionStorage(): { productProperties: ProductProperties; campaignId?: string } | null {
        const directPurchaseData = sessionStorage.getItem(SessionStorageItems.DIRECT_PURCHASE);
        if (directPurchaseData) {
            try {
                const parsed = JSON.parse(directPurchaseData);
                
                // Handle backward compatibility
                if (parsed.productProperties) {
                    // New format - DirectPurchaseData
                    return {
                        productProperties: parsed.productProperties,
                        campaignId: parsed.campaignId
                    };
                } else {
                    // Legacy format - treat as ProductProperties directly
                    return {
                        productProperties: parsed as ProductProperties,
                        campaignId: undefined
                    };
                }
            } catch (error) {
                console.error('Error parsing direct purchase data:', error);
                this.clearDirectPurchaseFromSessionStorage();
                return null;
            }
        }
        return null;
    }

    clearDirectPurchaseFromSessionStorage(): void {
        sessionStorage.removeItem(SessionStorageItems.DIRECT_PURCHASE);
    }

    hasDirectPurchaseInSessionStorage(): boolean {
        return sessionStorage.getItem(SessionStorageItems.DIRECT_PURCHASE) !== null;
    }
}